import api_fetch from '../utils/api_fetch';
import { storageHandler } from '../utils/storage_handler';
import createServiceClient from './createServiceClient';

const packageManagerService = {
  async getPackageDetailsList(page = 1, limit = 20) {
    const offset = (page - 1) * limit;
    const params = new URLSearchParams({ offset, limit });

    return (
      await api_fetch.get(
        `/v1/get-packages?${params.toString()}`,
        {
          Authorization: `Bearer ${storageHandler.localStorageGet('token')}`
        },
        {}
      )
    ).json();
  },

  async searchPackageDetails(
    filters = {},
    page = 1,
    sortBy,
    sortOrder,
    limit = 20
  ) {
    const offset = (page - 1) * limit;
    const params = new URLSearchParams();

    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    if (
      sortBy &&
      sortOrder &&
      sortBy !== 'default' &&
      sortOrder !== 'default'
    ) {
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
    }

    const response = await api_fetch.get(
      `/v1/search-packages?${params.toString()}`,
      {
        Authorization: `Bearer ${storageHandler.localStorageGet('token')}`
      },
      {}
    );

    return response.json();
  },

  async getPackageDetails(id) {
    return (
      await api_fetch.get(
        `/v1/get-package/${id}`,
        {
          Authorization: `Bearer ${storageHandler.localStorageGet('token')}`
        },
        {}
      )
    ).json();
  },

  async fetchAndProcessPackageSearch({
    jqlQuery = '',
    basicQuery = '',
    filters = [],
    dateFilter = 'default',
    dateRangeStart = '',
    dateRangeEnd = '',
    sortBy,
    sortOrder,
    page = 1,
    limit = 20,
    addressIds = [],
    includeUnmatched = false
  }) {
    const offset = (page - 1) * limit;
    const filterObj = {};

    // Basic text search — sent as `q`; mutually exclusive with JQL
    if (basicQuery && basicQuery.trim()) {
      filterObj.q = basicQuery.trim();
    } else if (jqlQuery && jqlQuery.trim()) {
      // JQL query string — sent as-is; the backend parses it
      filterObj.jql = jqlQuery.trim();
    }

    // Additional advanced-search filters
    filters.forEach((f) => {
      if (f.field && f.value) {
        filterObj[f.field] = f.value;
      }
    });

    // Custom date range takes priority over preset date_filter
    if (dateRangeStart && dateRangeEnd) {
      filterObj.date_filter_start = dateRangeStart;
      filterObj.date_filter_end = dateRangeEnd;
    } else if (dateFilter !== 'default') {
      filterObj.date_filter = dateFilter;
    }

    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('offset', offset);

    Object.entries(filterObj).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    // Appended outside the filterObj loop above: that loop skips falsy values,
    // and an empty array is truthy — it would append `address_ids=` and fail
    // validation on the backend.
    if (addressIds.length > 0) {
      params.append('address_ids', addressIds.join(','));
    }
    if (includeUnmatched) {
      params.append('include_unmatched', 'true');
    }

    if (
      sortBy &&
      sortOrder &&
      sortBy !== 'default' &&
      sortOrder !== 'default'
    ) {
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
    }

    const response = await api_fetch.get(
      `/v1/search-packages?${params.toString()}`,
      {
        Authorization: `Bearer ${storageHandler.localStorageGet('token')}`
      },
      {}
    );

    return response.json();
  },

  /**
   * One tile per address row, with how many packages are assigned to it.
   * Paginated, searched and sorted server-side.
   */
  async getPackagesByAddressSummary({
    page = 1,
    limit = 12,
    search = '',
    sortBy = 'count',
    sortOrder = 'DESC'
  } = {}) {
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('offset', (page - 1) * limit);
    if (search && search.trim()) params.append('search', search.trim());
    if (sortBy) params.append('sort_by', sortBy);
    if (sortOrder) params.append('sort_order', sortOrder);

    return (
      await api_fetch.get(
        `/v1/packages-address-summary?${params.toString()}`,
        {
          Authorization: `Bearer ${storageHandler.localStorageGet('token')}`
        },
        {}
      )
    ).json();
  },

  /**
   * Force a full recount: re-run the address matcher against every package
   * (or just the currently-unmatched ones) and persist any changed match.
   * Superadmin-only on the backend — see backend/routes/package-details.routes.js.
   */
  async recomputeAddressMatches({ onlyUnmatched = false } = {}) {
    return (
      await api_fetch.post(
        '/v1/recompute-address-matches',
        {
          Authorization: `Bearer ${storageHandler.localStorageGet('token')}`
        },
        onlyUnmatched ? { only_unmatched: 'true' } : {},
        '',
        {}
      )
    ).json();
  },

  async getPackagesByDate(
    dateType,
    page = 1,
    limit = 20,
    additionalFilters = {},
    sortBy = null,
    sortOrder = null
  ) {
    const offset = (page - 1) * limit;
    const params = new URLSearchParams({ offset, limit });

    // Add any additional filters
    Object.entries(additionalFilters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    // Add sorting parameters if provided
    if (
      sortBy &&
      sortOrder &&
      sortBy !== 'default' &&
      sortOrder !== 'default'
    ) {
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
    }

    return (
      await api_fetch.get(
        `/v1/packages-by-date/${encodeURIComponent(dateType)}?${params.toString()}`,
        {
          Authorization: `Bearer ${storageHandler.localStorageGet('token')}`
        },
        {}
      )
    ).json();
  }
};

/**
 * New service client using factory pattern
 * Provides standard methods with centralized boilerplate
 */
export const packageServiceClient = createServiceClient({
  baseEndpoint: '/v1/get-packages',
  searchEndpoint: '/v1/search-packages',
  customMethods: {
    // Alias for backward compatibility with different naming
    getPackageDetailsList: (client) => client.getList,
    searchPackageDetails: (client) => client.search,
    getPackageDetails: (client) => client.getById,

    // Complex search method with preprocessing
    fetchAndProcessPackageSearch: (client) => async ({
      searchValue,
      filters = [],
      dateFilter = 'default',
      dateRangeStart = '',
      dateRangeEnd = '',
      sortBy,
      sortOrder,
      page = 1,
      limit = 20
    }) => {
      const filterObj = {};

      // Transform filters array to object
      filters.forEach((f) => {
        if (f.field && f.value) {
          filterObj[f.field] = f.value;
        }
      });

      // Custom date range takes priority over preset date_filter
      if (dateRangeStart && dateRangeEnd) {
        filterObj.date_filter_start = dateRangeStart;
        filterObj.date_filter_end = dateRangeEnd;
      } else if (dateFilter !== 'default') {
        filterObj.date_filter = dateFilter;
      }

      if (searchValue) {
        filterObj.tracking_number = searchValue;
      }

      return client.search(filterObj, page, sortBy, sortOrder, limit);
    },

    // Get packages by date with filters and sorting
    getPackagesByDate: (client) => async (
      dateType,
      page = 1,
      limit = 20,
      additionalFilters = {},
      sortBy = null,
      sortOrder = null
    ) => {
      const { offset, limit: lim } = client.helpers.getPaginationOffset(page, limit);
      const params = client.helpers.buildQueryParams({ offset, limit: lim });

      client.helpers.applyFilters(params, additionalFilters);
      client.helpers.applySorting(params, sortBy, sortOrder);

      return client.rawGet(`/v1/packages-by-date/${encodeURIComponent(dateType)}?${params.toString()}`);
    }
  }
});

export default packageManagerService;
